/**
 * Extract only digits, one decimal point, and optional negative sign at the start
 * Used for decimal inputs that can be negative (e.g., prices, percentages)
 * @param value - Input string value
 * @param allowNegative - Whether to allow negative values (default: false)
 * @returns String with only digits, one decimal point, and optional leading negative sign
 */
export const getDecimalOnly = (
  value: string | undefined | null,
  allowNegative: boolean = false
): string => {
  if (!value) return '';

  const trimmed = value.trim();
  
  // Check if value starts with negative sign (if allowed)
  const isNegative = allowNegative && trimmed.startsWith('-');
  
  // Remove negative sign temporarily for processing
  const withoutSign = isNegative ? trimmed.slice(1) : trimmed;
  
  // Extract digits and decimal points
  const digitsAndDecimal = withoutSign.replace(/[^\d.]/g, '');
  
  // Handle multiple decimal points - keep only the first one
  const parts = digitsAndDecimal.split('.');
  let result = parts[0] || '';
  
  // Add decimal point and remaining digits if there was at least one decimal point
  if (parts.length > 1) {
    // Join all parts after the first one, then take only digits
    const afterDecimal = parts.slice(1).join('').replace(/\D/g, '');
    if (afterDecimal) {
      result += '.' + afterDecimal;
    } else if (parts[1] === '' || parts.length > 2) {
      // If there's a decimal point but no digits after, or multiple decimal points
      result += '.';
    }
  }
  
  // Return with negative sign if original value started with it and negative is allowed
  return isNegative ? `-${result}` : result;
};

